import { useQuery } from '@tanstack/react-query';
import {
  getUserRewardsAccounts,
  GetUserRewardsAccountsResponse,
} from '@leagueplatform/rewards-api';

/**
 * Hook for fetching user rewards accounts.
 *
 * This hook uses React Query's `useQuery` to fetch user rewards accounts
 * via the rewards API. It provides data, loading states, and error handling.
 *
 * @param units - Optional array of unit strings to filter the rewards accounts by
 * @returns Object containing:
 *   - `rewardAccounts`: The user rewards accounts data
 *   - `isLoading`: Loading state indicator (from isFetching)
 *   - `isError`: Error state indicator
 *   - `error`: Error object if request fails
 *   - `refetch`: Function to manually refetch the data
 *
 * @example
 * // Fetch all user rewards accounts
 * const { rewardAccounts, isLoading, isError, error } = useGetUserRewardsAccounts();
 *
 * // Fetch only specific unit types
 * const { rewardAccounts, isLoading, isError, error } = useGetUserRewardsAccounts(['points', 'tokens']);
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (isError) return <div>Error: {error.message}</div>;
 *
 * return (
 *   <div>
 *     {rewardAccounts?.data?.map(account => (
 *       <div key={account.id}>
 *         Balance: {account.balance} {account.rewardsAccount.data.unit}
 *       </div>
 *     ))}
 *   </div>
 * );
 */
export const useGetUserRewardsAccounts = (units?: string[]) => {
  const { data, isFetching, isError } = useQuery<
    GetUserRewardsAccountsResponse,
    Error
  >(['getUserRewardsAccounts', units], () => getUserRewardsAccounts(units));

  return {
    rewardAccounts: data,
    isLoading: isFetching,
    isError,
  };
};
